import { useState, useEffect } from "react";
import { Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { fetchPriceAnomalies } from "../lib/api";
import { remoteCardImageSrc } from "../lib/images";

// Vue "Anomalies de prix" : cartes dont le prix (normal) s'écarte fortement
// de la médiane de leur propre groupe (même set + même rareté) — la commune
// à plusieurs euros au milieu de communes à quelques centimes, ou l'inverse.
// Calculé côté backend sur tout le cache, mis en cache 24h là-bas.
export const PriceAnomaliesView = ({ onCardClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchPriceAnomalies()
      .then(setData)
      .catch(err => console.error("Anomalies de prix :", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-red-500" /></div>;
  }

  const card = (c, kind) => (
    <button key={c.id} onClick={() => onCardClick?.({ id: c.id })}
      className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-left hover:border-zinc-600 transition-colors">
      <img src={remoteCardImageSrc(c)} alt="" className="w-10 h-14 object-contain rounded shrink-0"
        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-white truncate">{c.name}</p>
        <p className="text-xs text-zinc-500 truncate">{c.set_name} · {c.rarity} · #{c.localId}</p>
      </div>
      <div className="text-right shrink-0">
        <p className={`text-sm font-bold ${kind === "high" ? "text-red-400" : "text-emerald-400"}`}>
          {c.price.toFixed(2)} €
        </p>
        <p className="text-[11px] text-zinc-500">
          médiane {c.group_median.toFixed(2)} € · ×{c.ratio}
        </p>
      </div>
    </button>
  );

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-1">Anomalies de prix</h2>
      <p className="text-sm text-zinc-500 mb-6">
        Cartes dont le prix s'écarte fortement de la médiane de leur groupe
        (même extension, même rareté). Recalculé une fois par jour.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-red-400" />
            Anormalement chères ({data.expensive.length})
          </h3>
          {data.expensive.length === 0 ? (
            <p className="text-sm text-zinc-500">Rien d'anormal détecté.</p>
          ) : (
            <div className="space-y-2">{data.expensive.map(c => card(c, "high"))}</div>
          )}
        </div>
        <div>
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-emerald-400" />
            Anormalement bon marché ({data.underpriced.length})
          </h3>
          {data.underpriced.length === 0 ? (
            <p className="text-sm text-zinc-500">Rien d'anormal détecté.</p>
          ) : (
            <div className="space-y-2">{data.underpriced.map(c => card(c, "low"))}</div>
          )}
        </div>
      </div>
    </div>
  );
};
